import { Component, inject, ViewChild } from '@angular/core';
import { RouterModule } from '@angular/router';
import { DurationPickerInputComponent } from "../duration-picker-input/duration-picker-input.component";
import { FormsModule, NgForm } from '@angular/forms';
import { DriverTimerSettings } from '../../../../_shared/models/drivers/operations/driver-timer-settings.model';
import { AccountSelectorComponent } from "../../../../_shared/components/account-selector/account-selector.component";
import { Account } from '../../../../_shared/models';
import { DriverTimerSettingsService } from '../../../dashboard/services/driver-timer-settings.service';
import { BlockUI, BlockUIModule, NgBlockUI } from 'ng-block-ui';
import { ErrorMessages } from '../../../../_shared/constants';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-driver-manager-settings',
  standalone: true,
  imports: [RouterModule, DurationPickerInputComponent, FormsModule, AccountSelectorComponent, BlockUIModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './driver-manager-settings.component.html',
  styleUrl: './driver-manager-settings.component.scss'
})
export class DriverManagerSettingsComponent {
  @BlockUI() blockUI!: NgBlockUI;
  @ViewChild('settingsForm') settingsForm!: NgForm;

  accountId: number;
  driverTimerSettings: DriverTimerSettings = {
    id: 0,
    accountId: 0,
    yardCheckInTimeThreshold: 0,
    yardEnterVehicleThreshold: 0,
    vehicleEnterThreshold: 0,
    vehicleExitYardThreshold: 0,
    drivingTimeThreshold: 0,
    drivingTimeDailyThreshold: 0,
    stationaryTimeThreshold: 0,
    stationaryTimeDailyThreshold: 0,
    idleTime: false
  };

  private driverTimerSettingsService = inject(DriverTimerSettingsService);
  private toastr = inject(ToastrService);
  private transloco = inject(TranslocoService);

  onAccountChange(account: Account): void {
    this.accountId = account?.id
    if (this.accountId) {
      this.loadAccountSettings();
    }
  }

  async loadAccountSettings(): Promise<void> {
    try {
      this.blockUI.start();
      this.resetDriverSettings();
      const response = await this.driverTimerSettingsService.getDriverTimerSettingsByAccountId(this.accountId);
      if (response.data) {
        this.driverTimerSettings = response.data;
      }
    } catch (e: any) {
      const errorCode = e?.error?.errorCode || 'errors/default';
      const message = ErrorMessages[errorCode];
      this.toastr.error(message);
    } finally {
      this.blockUI.stop();
    }
  }

  async submit(): Promise<void> {
    this.driverTimerSettings.accountId = this.accountId;
    try {
      this.blockUI.start();
      const response = await this.driverTimerSettingsService.upsertDriverTimerSettings(this.driverTimerSettings);
      if (response.data) {
        this.driverTimerSettings = response.data;
      }

      this.toastr.success(this.transloco.translate('driverManager.toasts.settingsUpdated'));
    } catch (e: any) {
      const errorCode = e?.error?.errorCode || 'errors/default';
      const message = ErrorMessages[errorCode];
      this.toastr.error(message);
    } finally {
      this.blockUI.stop();
    }
  }

  hasDurationError(elementName: string): boolean {
    return this.settingsForm?.controls && this.settingsForm.controls[elementName]?.touched
      && this.settingsForm.controls[elementName]?.errors
      && this.settingsForm.controls[elementName]?.errors!['invalidDuration']
  }

  resetDriverSettings(): void {
    this.driverTimerSettings = {
      id: 0,
      accountId: 0,
      yardCheckInTimeThreshold: 0,
      yardEnterVehicleThreshold: 0,
      vehicleEnterThreshold: 0,
      vehicleExitYardThreshold: 0,
      drivingTimeThreshold: 0,
      drivingTimeDailyThreshold: 0,
      stationaryTimeThreshold: 0,
      stationaryTimeDailyThreshold: 0,
      idleTime: false
    };

    Object.keys(this.driverTimerSettings).forEach((key) => {
      if (this.settingsForm.controls[key]) {
        this.settingsForm.controls[key].markAsUntouched();
      }
    });
  }
}